import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, CircularProgress } from '@mui/material';

import PoseTracking from './PoseTracking';
import { getAsana } from '../../services/asana/getAsana';

const AsanaSession = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    courseId,
    studentId,
    sequenceType = 'continuous',
    selectedPoseId
  } = location.state || {};

  const [currentIndex, setCurrentIndex] = useState(0);
  const [allSessionData, setAllSessionData] = useState([]);

  /* ---------------- API ---------------- */
  const { data, isLoading } = useQuery({
    queryKey: ['asanas', Number(courseId), Number(studentId)],
    queryFn: () =>
      getAsana({
        courseId: Number(courseId),
        studentId: Number(studentId)
      }),
    enabled: !!courseId
  });

  const allPoses = data?.response || [];

  /* ---------------- FILTER POSES ---------------- */
  const displayPoses = useMemo(() => {
    // 🔹 Standalone → exactly one pose
    if (sequenceType === 'standalone' && selectedPoseId) {
      return allPoses.filter(
        (p) =>
          p.id === Number(selectedPoseId)      );
    }

    // 🔹 Continuous → all pending poses
    return allPoses
      .filter(
        (p) =>
          p.sequenceType === 'continuous' &&
          p.asanaStatus === 1 &&
          p.completed === 0
      )
      .sort(
        (a, b) =>
          (a.posturenumber ?? 0) - (b.posturenumber ?? 0)
      );
  }, [allPoses, sequenceType, selectedPoseId]);

  /* ---------------- SAFETY NAVIGATION ---------------- */
  useEffect(() => {
    if (!isLoading && !displayPoses.length) {
      navigate(`/asanas/${courseId}`);
    }
  }, [displayPoses, isLoading, navigate, courseId]);



  

  /* ---------------- NEXT POSE HANDLER ---------------- */
  const handleNextPose = (poseResult) => {
  const updatedSessionData = [...allSessionData, poseResult];
  setAllSessionData(updatedSessionData);

  // ✅ Standalone = single pose → finish
  if (sequenceType === 'standalone') {
    navigate(`/asanas/${courseId}`, {
      state: {
        completed: true,
        sessionData: poseResult,
        poseId: poseResult.poseId
      }
    });
    return;
  }

  // ✅ Continuous flow
  const isLastPose = currentIndex === displayPoses.length - 1;

  if (isLastPose) {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }

    navigate(`/asanas/${courseId}/summary`, {
      state: {
        allSessionData: updatedSessionData,
        courseId
      }
    });
    return;
  }

  setCurrentIndex((prev) => prev + 1);
};



  /* ---------------- RENDER ---------------- */
  return (
    <PoseTracking
      pose={displayPoses[currentIndex]}
      onNextPose={handleNextPose}
      currentPoseIndex={currentIndex}
      totalPoses={displayPoses.length}
      sequenceType={sequenceType}
      isFlowSequence={sequenceType === 'continuous'}
    />
  );
};

export default AsanaSession;
